import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LoaderSVG from "../../components/LoaderSVG/LoaderSVG";
import InvestmentsPaginator from "../../components/InvestmentsPaginator/InvestmentsPaginator";

import { Filters, PaymentHistoryCard } from "./components";
import {
  usePaymentHistoryData,
  usePaymentHistoryFilters,
  usePaymentHistoryActions
} from "./hooks";
import {
  Container,
  LoadingContainer,
  LoadingText,
  PageTitle,
  PageContent,
  PaymentsList,
  NoResultsContainer,
  NoResultsIcon,
  NoResultsText,
  BackButton,
  BackButtonImage,
  BackContainer
} from "./styles";

const PaymentsHistory = () => {
  const { t } = useTranslation();
  const [hasSearched, setHasSearched] = useState(false);

  const {
    campaigns,
    paymentsList,
    totalItems,
    isFetching,
    isLoading,
    loadPayments
  } = usePaymentHistoryData();

  const {
    params,
    isClearFilter,
    onFilter,
    cleanFilters,
    resetClearFlag,
    setPage,
    setPageSize
  } = usePaymentHistoryFilters();

  const { handleBack, handleDownload } = usePaymentHistoryActions();

  const [valueSelected, setValueSelected] = useState(1);

  // Carga inicial
  useEffect(() => {
    loadPayments(params);
  }, []);

  // Recargar al limpiar filtros
  useEffect(() => {
    if (isClearFilter) {
      resetClearFlag();
      setHasSearched(false);
      loadPayments(params);
    }
  }, [isClearFilter]);

  const handleFilter = (filterValues) => {
    setHasSearched(true);
    const updatedParams = onFilter(filterValues);
    loadPayments(updatedParams);
  };

  const handleCleanFilters = () => {
    cleanFilters();
  };

  const handlePageChange = (page) => {
    setPage(page);
    loadPayments({ ...params, currentPage: page });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    loadPayments({ ...params, pageSize: size, currentPage: 1 });
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / params.pageSize));
  const paginatorData = {
    totalItems,
    currentPage: params.currentPage,
    totalPages,
    prevPage: params.currentPage > 1 ? params.currentPage - 1 : 0,
    nextPage: params.currentPage < totalPages ? params.currentPage + 1 : 0
  };

  const renderPaymentsList = () =>
    paymentsList.map((payment) => (
      <PaymentHistoryCard
        key={payment.id}
        payment={payment}
        onDownload={handleDownload}
      />
    ));

  const SearchIcon = () => (
    <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M62.7144 59.6698H66.1021L87.5 81.1106L81.1106 87.5L59.6698 66.1021V62.7144L58.512 61.5137C53.6235 65.7161 47.277 68.2461 40.3731 68.2461C24.9786 68.2461 12.5 55.7676 12.5 40.3731C12.5 24.9786 24.9786 12.5 40.3731 12.5C55.7676 12.5 68.2461 24.9786 68.2461 40.3731C68.2461 47.277 65.7161 53.6235 61.5137 58.512L62.7144 59.6698ZM21.0763 40.3731C21.0763 51.0506 29.6955 59.6698 40.3731 59.6698C51.0506 59.6698 59.6698 51.0506 59.6698 40.3731C59.6698 29.6955 51.0506 21.0763 40.3731 21.0763C29.6955 21.0763 21.0763 29.6955 21.0763 40.3731Z" fill="#7C878E"/>
    </svg>
  );

  const renderNoPaymentsMessage = () => {
    const message = hasSearched
      ? t("paymentsHistory.noSearchResults")
      : t("paymentsHistory.noPayments");
    return (
      <NoResultsContainer>
        <NoResultsIcon><SearchIcon /></NoResultsIcon>
        <NoResultsText>{message}</NoResultsText>
      </NoResultsContainer>
    );
  };

  return (
    <Container className="container">
      <PageContent>
        <BackContainer>
          <BackButton data-testid="back-button" onClick={handleBack}>
            <BackButtonImage alt="previous" src="/assets/images/previous.png" />
          </BackButton>
        </BackContainer>
        <PageTitle>{t("paymentsHistory.pageTitle")}</PageTitle>
        <Filters
          campaigns={campaigns}
          onFilter={handleFilter}
          cleanFilters={handleCleanFilters}
        />
        {isLoading || isFetching ? (
          <LoadingContainer>
            <LoaderSVG />
            <LoadingText>{t("paymentsHistory.loading")}</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            <PaymentsList>
              {Array.isArray(paymentsList) && paymentsList.length > 0
                ? renderPaymentsList()
                : renderNoPaymentsMessage()}
            </PaymentsList>
            <InvestmentsPaginator
              paginatorData={paginatorData}
              setNumberPage={handlePageChange}
              setPageSize={handlePageSizeChange}
              valueSelected={valueSelected}
              setValueSelected={setValueSelected}
              isDisabled={!Array.isArray(paymentsList) || paymentsList.length === 0}
            />
          </>
        )}
      </PageContent>
    </Container>
  );
};

export default PaymentsHistory;
